import React from "react";
import {
  X,
  ExternalLink,
  Calendar,
  DollarSign,
  Tag,
  TrendingUp,
  Globe,
  ShoppingCart,
  Users,
  Activity,
  Award,
  Link as LinkIcon,
} from "react-feather";
import { AdminCampaign } from "../../services/admin/campaigns.admin.service";
import { formatCurrency, formatDate } from "../../utils/formatters";

interface AdminCampaignDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: AdminCampaign | null;
}

export function AdminCampaignDetailModal({
  isOpen,
  onClose,
  campaign,
}: AdminCampaignDetailModalProps) {
  if (!isOpen || !campaign) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10";
      case "PAUSED":
        return "text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-500/10";
      case "COMPLETED":
        return "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10";
      case "INACTIVE":
        return "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10";
      default:
        return "text-muted-foreground bg-muted";
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-card rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-muted">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-500/15 rounded-full flex items-center justify-center">
              <Tag className="w-5 h-5 text-[#d7785b]" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Campaign Details
              </h2>
              <p className="text-sm text-muted-foreground">
                View campaign information and performance
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-muted-foreground hover:bg-accent rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-6">
            {/* Campaign Overview */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {campaign.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>Created {formatDate(campaign.createdAt)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <LinkIcon className="w-4 h-4" />
                      <span className="font-mono bg-muted px-2 py-1 rounded text-xs">
                        {campaign.code}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                      campaign.status
                    )}`}
                  >
                    {campaign.status}
                  </span>
                  {campaign.link && (
                    <a
                      href={campaign.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-[#d7785b] text-white rounded-lg hover:bg-[#c86c51] transition-colors text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Campaign
                    </a>
                  )}
                </div>
              </div>

              {/* Tags */}
              {campaign.tags && (
                <div className="mb-4">
                  <div className="bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-[#d7785b]" />
                      <span className="text-sm font-medium text-orange-800 dark:text-orange-300">
                        Campaign Details
                      </span>
                    </div>
                    <p className="text-sm text-orange-700 dark:text-orange-300">{campaign.tags}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Performance Metrics */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-muted-foreground" />
                Performance Metrics
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-green-50 dark:bg-green-500/10 p-4 rounded-lg text-center border border-green-200 dark:border-green-500/30">
                  <div className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">
                    Total Sales
                  </div>
                  <div className="text-xl font-bold text-green-900">
                    {formatCurrency(campaign.totalSales)}
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    Revenue Generated
                  </div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-500/10 p-4 rounded-lg text-center border border-blue-200 dark:border-blue-500/30">
                  <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">
                    Sales Count
                  </div>
                  <div className="text-xl font-bold text-blue-900">
                    {campaign.salesCount}
                  </div>
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">Total Orders</div>
                </div>
                <div className="bg-orange-50 dark:bg-orange-500/10 p-4 rounded-lg text-center border border-orange-200 dark:border-orange-500/30 sm:col-span-2 lg:col-span-1">
                  <div className="text-sm text-[#d7785b] font-medium mb-1">
                    Avg. Order Value
                  </div>
                  <div className="text-xl font-bold text-orange-900 dark:text-orange-200">
                    {formatCurrency(
                      campaign.salesCount > 0
                        ? campaign.totalSales / campaign.salesCount
                        : 0
                    )}
                  </div>
                  <div className="text-xs text-[#d7785b] mt-1">
                    Per Transaction
                  </div>
                </div>
              </div>
            </div>

            {/* Brand Information */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-muted-foreground" />
                Brand Information
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Brand Name:</span>
                    <span className="text-sm font-medium text-foreground">
                      {campaign.brandName}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Website:</span>
                    <a
                      href={campaign.brandWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#d7785b] hover:text-[#c86c51] underline"
                    >
                      {campaign.brandWebsite}
                    </a>
                  </div>
                  {campaign.shopDomain && (
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Shop Domain:
                      </span>
                      <span className="text-sm font-medium text-foreground">
                        {campaign.shopDomain}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Campaign Configuration */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-muted-foreground" />
                Campaign Configuration
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Target Products:
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {campaign.targetAllProducts}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Discount Type:
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {campaign.discountType === "PERCENTAGE"
                        ? "Percentage"
                        : "Fixed Amount"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Discount Value:
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {campaign.discountType === "PERCENTAGE"
                        ? `${campaign.discountValue}%`
                        : formatCurrency(campaign.discountValue)}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Usage Limit:</span>
                    <span className="text-sm font-medium text-foreground">
                      {campaign.usageLimit}
                    </span>
                  </div>
                  {campaign.minimumPurchaseAmount && (
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Minimum Purchase:
                      </span>
                      <span className="text-sm font-medium text-foreground">
                        {formatCurrency(campaign.minimumPurchaseAmount)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Applies on Each Item:
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {campaign.appliesOnEachItem ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Minimum Requirements */}
              {campaign.minimumRequirement &&
                campaign.minimumRequirement !== "No minimum requirement" && (
                  <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                      <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                        Requirements
                      </span>
                    </div>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      {campaign.minimumRequirement}
                    </p>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-3 p-6 border-t border-border bg-muted">
          <button
            onClick={onClose}
            className="px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
